import {
  cacheMiddleware,
  MiddlewareNextFn,
  RelayNetworkLayer,
  RelayRequestAny,
  RRNLRequestError,
  urlMiddleware,
} from 'react-relay-network-modern';
import { Environment, RecordSource, Store } from 'relay-runtime';
import { v4 as uuid } from 'uuid';
import {
  getUiSessionId,
  JOURNEY_HEADER_KEY,
  JOURNEY_SESSION_KEY,
} from '@sparknz/journeys-common';

import { isBrowser } from './ssr.utils';

export interface RRNLRequestErrorWithExtensions extends RRNLRequestError {
  res?: RRNLRequestError['res'] & {
    errors: {
      extensions?: {
        response: {
          status: number;
          body?: {
            messages: {
              code: number;
              message: string;
            }[];
          };
        };
      };
    }[];
  };
}

let relayEnvironment: Environment;

interface MakeEnvironmentParameter {
  graphqlURL: string;
  sessionId?: string;
  serverSideJourneyName?: string;
}

const isCI = process.env.CI === 'true';

export function setJourneyName(newJourneyName?: string) {
  if (isBrowser()) {
    if (!newJourneyName) {
      window.sessionStorage?.removeItem(JOURNEY_SESSION_KEY);
      return;
    }
    window.sessionStorage?.setItem(JOURNEY_SESSION_KEY, newJourneyName);
  }
}

export function getJourneyName() {
  if (isBrowser()) {
    const sessionJourneyName =
      window.sessionStorage?.getItem(JOURNEY_SESSION_KEY);
    return sessionJourneyName === 'undefined' ? null : sessionJourneyName;
  }

  return null;
}

function buildHeadersMiddleware(
  sessionId?: string,
  serverSideJourneyName?: string,
) {
  return (next: MiddlewareNextFn) => (req: RelayRequestAny) => {
    const journeyName = serverSideJourneyName || getJourneyName();
    if (process.env.NODE_ENV === 'production') {
      req.fetchOpts.credentials = 'include';
    }
    if (journeyName) {
      req.fetchOpts.headers[JOURNEY_HEADER_KEY] = journeyName;
    }
    if (sessionId) {
      req.fetchOpts.headers['X-Dasl-Session-ID'] = sessionId;
    } else if (isCI) {
      req.fetchOpts.headers['X-Dasl-Session-ID'] = uuid();
    } else if (isBrowser()) {
      req.fetchOpts.headers['X-DASL-SESSION-ID'] = getUiSessionId();
    }

    return next(req);
  };
}

function failedTransactionIdLoggerMiddleware() {
  return (next: MiddlewareNextFn) => async (req: RelayRequestAny) => {
    const res = await next(req);
    if (res.errors && isBrowser()) {
      // eslint-disable-next-line no-console
      console.error(
        `Failed transction: [${req.fetchOpts.headers['X-Global-Transaction-ID']}] [${req.fetchOpts.headers['X-Dasl-Session-ID']}]`,
        res.errors[0],
      );
    }
    return res;
  };
}

export const makeRelayEnvironment = ({
  graphqlURL,
  sessionId,
  serverSideJourneyName,
}: MakeEnvironmentParameter) => {
  const store = new Store(new RecordSource(), {
    // This property tells Relay to not immediately clear its cache when the user
    // navigates around the app. Relay will hold onto the specified number of
    // query results, allowing the user to return to recently visited pages
    // and reusing cached data if its available/fresh.
    gcReleaseBufferSize: 10,
  });
  return new Environment({
    store,
    network: new RelayNetworkLayer([
      cacheMiddleware(),
      urlMiddleware({
        url: graphqlURL,
      }),
      buildHeadersMiddleware(sessionId, serverSideJourneyName),
      failedTransactionIdLoggerMiddleware(),
    ]),
  });
};

export function createRelayEnvironment(
  makeEnvironmentOptions: MakeEnvironmentParameter,
): Environment {
  if (!relayEnvironment) {
    relayEnvironment = makeRelayEnvironment(makeEnvironmentOptions);
  }

  return relayEnvironment;
}

export function resetRelayEnvironment() {
  relayEnvironment = null;
}
